import { useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import SleepForm from "../../components/forms/sleepForm";
import SleepTable from "../../components/tables/sleepTable";
import LoadingState from "../../components/ui/LoadingState";
import EmptyState from "../../components/ui/EmptyState";
import SectionSummaryBar from "../../components/ui/SectionSummaryBar";
import { useFirestore } from "../../hooks/useFirestore";
import { useChild } from "../../contexts/ChildContext";
import { useTimer } from "../../contexts/TimerContext";
import { toDateLocal, formatTimeAgo } from "../../utils/dateUtils";
import { getTodaySleepMinutes, getLastSleepEndTime, getTodayNapStats } from "../../utils/wakeWindowUtils";
import { exportToCSV, sleepCSVColumns } from "../../utils/exportUtils";
import { exportSectionPDF } from "../../utils/pdfUtils";

function timerToSleepValues(timerData) {
  if (!timerData) return null;
  const start = new Date(timerData.startTime);
  const end   = new Date(start.getTime() + timerData.elapsedSeconds * 1000);
  const pad   = (n) => String(n).padStart(2, '0');
  return {
    date:      toDateLocal(start),
    startTime: `${pad(start.getHours())}:${pad(start.getMinutes())}`,
    endTime:   `${pad(end.getHours())}:${pad(end.getMinutes())}`,
  };
}

function formatDuration(seconds) {
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
}

function formatMins(min) {
  if (!min) return '0m';
  const m = Math.round(min);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
}

const SleepTracking = () => {
  const { selectedChild } = useChild();
  const { items: sleepData, addItem, updateItem, deleteItem, loading, error } =
    useFirestore("sleepTracking", selectedChild?.id);
  const { timer, elapsed, start: startTimer } = useTimer();
  const location = useLocation();

  const timerData     = location.state?.timerData?.type === 'sleep' ? location.state.timerData : null;
  const initialValues = timerToSleepValues(timerData);
  const [noticeDismissed, setNoticeDismissed] = useState(false);

  // Today at a glance
  const sleepStats = useMemo(() => {
    const todaySleepMin = getTodaySleepMinutes(sleepData);
    const { count: napCount } = getTodayNapStats(sleepData);
    const lastWoke = getLastSleepEndTime(sleepData);
    return { todaySleepMin, napCount, lastWoke };
  }, [sleepData]);

  const summaryStats = useMemo(() => {
    if (!sleepData.length) return null;
    return [
      { value: formatMins(sleepStats.todaySleepMin), label: 'sleep today' },
      { value: String(sleepStats.napCount), label: sleepStats.napCount === 1 ? 'nap today' : 'naps today' },
      { value: formatTimeAgo(sleepStats.lastWoke) ?? '—', label: 'last woke' },
    ];
  }, [sleepData, sleepStats]);

  const handleAddSleep = async (sleep) => {
    try {
      await addItem({ ...sleep, childId: selectedChild?.id });
    } catch (err) {
      console.error("Error adding sleep entry:", err);
    }
  };

  const handleExport = () =>
    exportToCSV(sleepData, `sleep-${selectedChild?.name || "export"}.csv`, sleepCSVColumns);

  const handleExportPDF = () =>
    exportSectionPDF(sleepData, `sleep-${selectedChild?.name || "export"}.pdf`, sleepCSVColumns, "Sleep Tracking", selectedChild?.name || "");

  const thisSleepTimerRunning = timer?.type === 'sleep';

  return (
    <div className="card h-100" id="sleep-tracking">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0">🛏️ Sleep Tracking</h5>
          <div className="d-flex gap-1 align-items-center flex-wrap">
            {thisSleepTimerRunning ? (
              <span className="timer-running-badge">
                <span style={{ animation: 'timer-dot-pulse 1.4s ease-in-out infinite', display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: 'var(--primary-dark)', marginRight: 2 }} aria-hidden="true" />
                {Math.floor(elapsed / 60)}m running
              </span>
            ) : !timer ? (
              <button
                className="timer-start-btn"
                onClick={() => startTimer('sleep')}
                title="Start a live sleep timer"
              >
                ▶ Start Timer
              </button>
            ) : null}
            <button className="btn btn-sm btn-outline-secondary" onClick={handleExport}>⬇ CSV</button>
            <button className="btn btn-sm btn-outline-primary" onClick={handleExportPDF}>⬇ PDF</button>
          </div>
        </div>

        {!loading && summaryStats && <SectionSummaryBar stats={summaryStats} />}

        {timerData && !noticeDismissed && (
          <div className="timer-prefill-notice">
            <span>⏱</span>
            <span>Form pre-filled from timer — <strong>{formatDuration(timerData.elapsedSeconds)}</strong> session</span>
            <button
              className="timer-prefill-dismiss"
              onClick={() => setNoticeDismissed(true)}
              aria-label="Dismiss notice"
            >✕</button>
          </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}
        <SleepForm onAdd={handleAddSleep} initialValues={initialValues} />
        {loading ? (
          <LoadingState message="Loading sleep tracking..." />
        ) : sleepData.length > 0 ? (
          <SleepTable data={sleepData} onUpdate={updateItem} onDelete={deleteItem} />
        ) : (
          <EmptyState
            icon="😴"
            heading="No sleep logged yet"
            message="Track the first sleep and start building a picture of your little one's patterns."
          />
        )}
      </div>
    </div>
  );
};

export default SleepTracking;
